import { Box, Fab, Tooltip, keyframes } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import React, { useState } from "react";

interface AiFabProps {
  openAI: boolean;
  setOpenAI: (next: boolean) => void;
  /**
   * Label shown next to the button on hover. Defaults to the Italian
   * "Assistente AI Flowenti" — pages with a translated drawer can pass
   * their own copy if they want to localize this string.
   */
  label?: string;
}

/**
 * Continuous soft glow that hints at interactivity even when the user
 * is not hovering. Slow enough to not be annoying (2.4s) and gentle in
 * amplitude so it reads as "alive" rather than "alert".
 */
const pulseGlow = keyframes`
  0%   { box-shadow: 0 6px 18px rgba(84, 189, 193, 0.35), 0 0 0 0 rgba(84, 189, 193, 0.45); }
  50%  { box-shadow: 0 6px 18px rgba(84, 189, 193, 0.35), 0 0 0 14px rgba(84, 189, 193, 0); }
  100% { box-shadow: 0 6px 18px rgba(84, 189, 193, 0.35), 0 0 0 0 rgba(84, 189, 193, 0); }
`;

const labelSlideIn = keyframes`
  from { transform: translateX(12px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

/**
 * Floating Action Button that opens the AI assistant drawer.
 *
 * Positioned in the bottom-right corner, just above the version chip.
 * The button hides itself when the drawer is already open so the user
 * is not offered a redundant "open" affordance.
 *
 * Visual behaviour:
 *  - Always-on pulsing glow to suggest interactivity.
 *  - On hover: the glow intensifies, the button scales up slightly,
 *    and a label slides in from the right naming the assistant.
 */
const AiFab: React.FC<AiFabProps> = ({
  openAI,
  setOpenAI,
  label = "Assistente AI Flowenti",
}) => {
  // Track hover locally so we can render the label without keeping
  // a persistent node in the DOM when not hovered.
  const [hovered, setHovered] = useState(false);

  if (openAI) {
    // Hiding cleanly is preferable to "pointer-events: none" because
    // the drawer itself occupies the right side of the viewport and
    // we don't want the FAB to bleed through with a tooltip on focus.
    return null;
  }

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "fixed",
        // 36px leaves breathing room above the version chip at bottom: 8px
        bottom: 36,
        right: 16,
        zIndex: 9999999999999,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {hovered && (
        <Box
          role="tooltip"
          aria-hidden
          sx={{
            backgroundColor: "background.paper",
            color: "primary.main",
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 2,
            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
            px: 1.5,
            py: 0.75,
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
            animation: `${labelSlideIn} 200ms ease-out`,
            pointerEvents: "none",
          }}
        >
          {label}
        </Box>
      )}
      <Tooltip title={label} placement="left" arrow>
        <Fab
          color="primary"
          aria-label={label}
          onClick={() => setOpenAI(true)}
          sx={{
            width: 56,
            height: 56,
            background: "linear-gradient(135deg, #54bdc1 0%, #1e355f 100%)",
            color: "common.white",
            boxShadow: "0 6px 18px rgba(84, 189, 193, 0.35)",
            animation: `${pulseGlow} 2400ms ease-in-out infinite`,
            transition: "transform 200ms ease, box-shadow 200ms ease",
            "&:hover": {
              transform: "scale(1.08)",
              boxShadow:
                "0 10px 24px rgba(84, 189, 193, 0.5), 0 0 0 8px rgba(84, 189, 193, 0.15)",
              animation: `${pulseGlow} 1600ms ease-in-out infinite`,
              background: "linear-gradient(135deg, #54bdc1 0%, #1e355f 100%)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
            "& .MuiSvgIcon-root": {
              fontSize: 28,
            },
          }}
        >
          <AutoAwesome />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default AiFab;
